import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  previewNotificationPublish, publishNotifications,
  previewNotificationSyncToDev, syncNotificationsToDev,
} from '../api';

const CHANNEL_LABELS = { alert: 'In-app', sms: 'SMS', email: 'Email' };
const FIELD_LABELS = {
  subject: 'Subject', body: 'Message', html_body: 'Email HTML', enabled: 'Custom copy enabled',
  send_enabled: 'Delivery', source: 'Copy source',
};

function fieldLabel(field, kind) {
  if (field === 'enabled' && kind === 'channel') return 'Delivery';
  return FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function DiffValue({ value }) {
  const text = value == null ? 'Not set' : value === '' ? 'Empty' : typeof value === 'boolean' ? (value ? 'On' : 'Off') : String(value);
  // Deliberately render HTML as text: the review must show all stored content
  // without executing template markup or loading remote resources.
  return (
    <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      {text}
    </Typography>
  );
}

function ChangeDiff({ change, selected, disabled, onSelect, blockers, copyToDev }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
        {!copyToDev && <Checkbox
          checked={selected}
          disabled={disabled}
          onChange={(event) => onSelect(change.id, event.target.checked)}
          inputProps={{ 'aria-label': `Select ${change.label || change.key} (${CHANNEL_LABELS[change.channel] || change.channel}) ${change.kind === 'channel' ? 'channel setting' : 'template'}` }}
        />}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{change.label || change.key}</Typography>
        <Chip size="small" label={CHANNEL_LABELS[change.channel] || change.channel} />
        <Chip size="small" variant="outlined" label={change.kind === 'channel' ? 'Channel setting' : 'Template'} />
        {change.protected && <Chip size="small" color="warning" label="Protected" />}
      </Box>
      <Typography variant="caption" component="div" color="text.secondary" sx={{ overflowWrap: 'anywhere', mb: 1 }}>
        {change.key}
      </Typography>
      {blockers.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {copyToDev ? 'This change cannot be copied.' : 'This change cannot be selected.'}
          <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
            {blockers.map((blocker, index) => <li key={index}>{blocker.message}</li>)}
          </Box>
        </Alert>
      )}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={`Changes for ${change.label || change.key}`} sx={{ tableLayout: 'fixed', minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Field</TableCell>
              <TableCell sx={{ width: '40%', fontWeight: 700 }}>Before · Current {copyToDev ? 'dev' : 'prod'}</TableCell>
              <TableCell sx={{ width: '40%', fontWeight: 700 }}>After · From {copyToDev ? 'prod' : 'dev'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {change.fields.map((field) => (
              <TableRow key={field.field} sx={{ '& > td, & > th': { verticalAlign: 'top' } }}>
                <TableCell component="th" scope="row">{fieldLabel(field.field, change.kind)}</TableCell>
                <TableCell><DiffValue value={field.before} /></TableCell>
                <TableCell sx={{ bgcolor: 'action.hover' }}><DiffValue value={field.after} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

function failedResponse(response, fallback) {
  return Object.assign(new Error(response?.message || fallback), { response: { data: response } });
}

export default function NotificationPublishDialog({ onClose, onPublished, mode = 'publish' }) {
  const copyToDev = mode === 'copy-to-dev';
  const [state, setState] = useState({ status: 'loading', preview: null });
  const [ackProtected, setAckProtected] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [expired, setExpired] = useState(false);
  const requestSeq = useRef(0);
  const requestController = useRef(null);
  const publishingRef = useRef(false);

  const loadPreview = useCallback(async () => {
    if (publishingRef.current) return;
    const seq = ++requestSeq.current;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    setAckProtected(false);
    setSelectedIds([]);
    setExpired(false);
    setState({ status: 'loading', preview: null });
    try {
      const response = await (copyToDev ? previewNotificationSyncToDev : previewNotificationPublish)({ signal: controller.signal });
      if (seq !== requestSeq.current) return;
      const preview = response?.data;
      if (!response?.success || !preview?.preview_id ||
          preview.source_environment !== (copyToDev ? 'production' : 'development') ||
          preview.target_environment !== (copyToDev ? 'development' : 'production') ||
          !Array.isArray(preview.changes) || !Array.isArray(preview.blockers) ||
          !Number.isFinite(Date.parse(preview.expires_at))) {
        throw failedResponse(response, `Could not load a complete ${copyToDev ? 'development copy' : 'production'} preview.`);
      }
      if (copyToDev && (preview.selection_required !== false || preview.sync_all_required !== true ||
          !Number.isFinite(preview.request_budget?.limit) || preview.request_budget.limit < 0 ||
          !Number.isFinite(preview.request_budget?.preview_requests) || preview.request_budget.preview_requests < 0 ||
          !Number.isFinite(preview.request_budget?.confirmation_detail_reads) || preview.request_budget.confirmation_detail_reads < 0)) {
        throw new Error('Copying production to development requires the updated reporting server. Reload this page after the server update is complete.');
      }
      if (!copyToDev && (preview.selection_required !== true || preview.sync_all_required === true)) {
        throw new Error('Selective publishing requires the updated reporting server. Reload this page after the server update is complete.');
      }
      setState({ status: 'ready', preview });
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setState({ status: 'error', preview: null, error: error?.response?.data?.message || error.message || 'Failed to compare development with production.' });
    }
  }, [copyToDev]);

  useEffect(() => {
    loadPreview();
    return () => {
      // Closing or switching environments invalidates every outstanding result.
      requestSeq.current += 1;
      requestController.current?.abort();
    };
  }, [loadPreview]);

  const preview = state.preview;
  const changes = preview?.changes || [];
  const blockers = preview?.blockers || [];
  const globalBlockers = blockers.filter((blocker) => !blocker.change_id);
  const scopedBlockers = blockers.filter((blocker) => blocker.change_id);
  const blockedIds = new Set(scopedBlockers.map((blocker) => blocker.change_id));
  const selectableChanges = changes.filter((change) => !blockedIds.has(change.id));
  const selectedChanges = copyToDev ? changes : changes.filter((change) => selectedIds.includes(change.id));
  const hasProtected = selectedChanges.some((change) => change.protected);
  const publishing = state.status === 'publishing';
  const selectionDisabled = state.status !== 'ready' || expired || (copyToDev ? blockers.length > 0 : globalBlockers.length > 0);
  const budget = preview?.request_budget;
  const verificationRequests = selectedChanges.filter((change) => change.kind === 'template').length * (budget?.verification_reads_per_template ?? 0);
  const availableRequests = Number.isFinite(budget?.remaining) ? budget.remaining : budget?.limit;
  const estimatedRequests = Number.isFinite(budget?.remaining) && Number.isFinite(budget?.confirmation_requests)
    ? budget.confirmation_requests + selectedChanges.length + verificationRequests
    : budget && Number.isFinite(budget.limit) && Number.isFinite(budget.preview_requests)
    ? budget.preview_requests + 2 + (copyToDev ? budget.confirmation_detail_reads : new Set(selectedChanges.flatMap((change) => change.template_keys || [])).size) + selectedChanges.length + verificationRequests
    : null;
  const overBudget = estimatedRequests !== null && estimatedRequests > availableRequests;
  const canPublish = !selectionDisabled && selectedChanges.length > 0 && !selectedChanges.some((change) => blockedIds.has(change.id)) && !overBudget && (!hasProtected || ackProtected);

  const updateSelection = (ids) => {
    if (selectionDisabled || publishingRef.current) return;
    setSelectedIds(ids);
    setAckProtected(false);
  };

  const selectChange = (id, checked) => {
    if (blockedIds.has(id)) return;
    updateSelection(checked ? [...selectedIds, id] : selectedIds.filter((selectedId) => selectedId !== id));
  };

  const describeChange = (id) => {
    const change = changes.find((item) => item.id === id);
    return change ? `${change.label || change.key} (${CHANNEL_LABELS[change.channel] || change.channel})` : id;
  };

  useEffect(() => {
    if (!preview) return undefined;
    const remaining = Date.parse(preview.expires_at) - Date.now();
    if (remaining <= 0) {
      setExpired(true);
      return undefined;
    }
    const timer = setTimeout(() => setExpired(true), remaining);
    return () => clearTimeout(timer);
  }, [preview]);

  const confirmPublish = async () => {
    if (!canPublish || publishingRef.current) return;
    if (Date.parse(preview.expires_at) <= Date.now()) {
      setExpired(true);
      return;
    }
    publishingRef.current = true;
    const seq = ++requestSeq.current;
    const controller = new AbortController();
    requestController.current = controller;
    setState((current) => ({ ...current, status: 'publishing', error: null }));
    try {
      const response = await (copyToDev ? syncNotificationsToDev : publishNotifications)({
        preview_id: preview.preview_id,
        ...(!copyToDev && { selected_change_ids: selectedChanges.map((change) => change.id) }),
        confirm: true,
        confirm_protected: hasProtected && ackProtected,
      }, { signal: controller.signal });
      if (seq !== requestSeq.current) return;
      if (!response?.success) throw failedResponse(response, copyToDev ? 'Copying production to development failed.' : 'Production publish failed.');
      onPublished(response);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      const details = error?.response?.data;
      const submittedIds = new Set(selectedChanges.map((change) => change.id));
      setState((current) => ({
        ...current,
        status: 'failed',
        error: details?.message || (copyToDev ? 'The copy result could not be confirmed. Development may have changed.' : 'The publish result could not be confirmed. Production may have changed.'),
        applied: Array.isArray(details?.data?.applied) ? details.data.applied.filter((id) => submittedIds.has(id)) : [],
        failedChange: submittedIds.has(details?.data?.failed_change) ? details.data.failed_change : null,
        remaining: Array.isArray(details?.data?.remaining) ? details.data.remaining.filter((id) => submittedIds.has(id)) : [],
      }));
      setAckProtected(false);
    } finally {
      publishingRef.current = false;
    }
  };

  const close = () => { if (!publishingRef.current) onClose(); };

  return (
    <Dialog open onClose={close} disableEscapeKeyDown={publishing} maxWidth="lg" fullWidth aria-labelledby="notification-publish-title">
      <DialogTitle id="notification-publish-title">{copyToDev ? 'Copy prod notifications to dev' : 'Publish notifications to prod'}</DialogTitle>
      <DialogContent dividers>
        {copyToDev ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            All notification templates and channel settings in development will be set to match production. This overwrites development edits. Production will remain unchanged. Review all changes before confirming.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Choose which saved development changes to publish to production. Only selected changes will be published.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A template’s subject, message, and email HTML are published together. Channel settings use existing production copy unless you also select the corresponding template update.
            </Typography>
          </>
        )}
        {state.status === 'loading' && (
          <Box role="status" sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} /> Comparing dev with prod…
          </Box>
        )}
        {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}
        {state.status === 'failed' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {state.applied.length > 0 && (
              <>
                <Typography variant="body2">{state.applied.length} change{state.applied.length === 1 ? ' was' : 's were'} already applied to {copyToDev ? 'development' : 'production'}:</Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                  {state.applied.map((id) => <li key={id}>{describeChange(id)}</li>)}
                </Box>
              </>
            )}
            {state.failedChange && (
              <Typography variant="body2" sx={{ mb: 1 }}>Could not confirm this change: {describeChange(state.failedChange)}.</Typography>
            )}
            {state.remaining.length > 0 && (
              <>
                <Typography variant="body2">{state.remaining.length} change{state.remaining.length === 1 ? ' was' : 's were'} not attempted:</Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                  {state.remaining.map((id) => <li key={id}>{describeChange(id)}</li>)}
                </Box>
              </>
            )}
            {!copyToDev && <Typography variant="body2" sx={{ mb: 1 }}>Unselected changes were not sent for publishing.</Typography>}
            Refresh the preview to check the current {copyToDev ? 'development' : 'production'} state before confirming again.
          </Alert>
        )}
        {expired && state.status === 'ready' && <Alert severity="warning" sx={{ mb: 2 }}>This preview has expired. Refresh it to review the latest changes.</Alert>}
        {globalBlockers.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{copyToDev ? 'Copying to dev is blocked' : 'Publishing is blocked'}</Typography>
            <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
              {globalBlockers.map((blocker, index) => <li key={`${blocker.key}-${index}`}>{blocker.key && `${blocker.key}: `}{blocker.message}</li>)}
            </Box>
          </Alert>
        )}
        {scopedBlockers.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {copyToDev
              ? 'All changes must be copied together. Resolve every error below before copying production to development.'
              : `Some changes cannot be published and are unavailable for selection. ${globalBlockers.length > 0 ? 'Resolve the publishing errors above before selecting updates.' : 'You can still select other changes.'} Review the errors below.`}
            {scopedBlockers.filter((blocker) => !changes.some((change) => change.id === blocker.change_id)).map((blocker, index) => (
              <Typography key={index} variant="body2">{blocker.key && `${blocker.key}: `}{blocker.message}</Typography>
            ))}
          </Alert>
        )}
        {(preview?.warnings || []).map((warning, index) => (
          <Alert key={index} severity="warning" sx={{ mb: 2 }}>{typeof warning === 'string' ? warning : warning.message}</Alert>
        ))}
        {preview && changes.length === 0 && !blockers.length && <Alert severity="success">{copyToDev ? 'Development already matches production. There are no changes to copy.' : 'Production is up to date. There are no changes to publish.'}</Alert>}
        {changes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {copyToDev ? `${changes.length} changes will be copied to development.` : `${selectedChanges.length} of ${changes.length} changes selected.`} Preview expires at {new Date(preview.expires_at).toLocaleTimeString()}.
            </Typography>
            {!copyToDev && <>
              <Button onClick={() => updateSelection(selectableChanges.map((change) => change.id))} disabled={selectionDisabled || selectedChanges.length === selectableChanges.length}>Select all</Button>
              <Button onClick={() => updateSelection([])} disabled={selectionDisabled || selectedChanges.length === 0}>Clear selection</Button>
            </>}
          </Box>
        )}
        {overBudget && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {copyToDev
              ? `Copying all changes exceeds the request limit (${estimatedRequests} of ${availableRequests}). No changes can be copied until this limit is resolved.`
              : `This selection exceeds the publishing request limit (${estimatedRequests} of ${availableRequests}). Select fewer changes to continue.`}
          </Alert>
        )}
        {changes.map((change) => (
          <ChangeDiff
            key={change.id}
            change={change}
            selected={selectedIds.includes(change.id)}
            disabled={selectionDisabled || blockedIds.has(change.id)}
            onSelect={selectChange}
            blockers={scopedBlockers.filter((blocker) => blocker.change_id === change.id)}
            copyToDev={copyToDev}
          />
        ))}
        {hasProtected && (
          <FormControlLabel
            control={<Checkbox checked={ackProtected} onChange={(event) => setAckProtected(event.target.checked)} disabled={selectionDisabled || overBudget} />}
            label={copyToDev
              ? 'I have reviewed all protected sign-in or payment notification changes above and confirm they should replace the development settings.'
              : 'I have reviewed the protected sign-in or payment notification changes selected above and confirm they are ready for production.'}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={publishing}>Cancel</Button>
        {state.status !== 'loading' && (
          <Button onClick={loadPreview} disabled={publishing}>Refresh preview</Button>
        )}
        <Button variant="contained" color="warning" onClick={confirmPublish} disabled={!canPublish}>
          {copyToDev ? (publishing ? 'Copying…' : 'Confirm copy to dev') : (publishing ? 'Publishing…' : 'Confirm publish to prod')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
